import type { BillingAddress, SetBillingAddressRequest } from '@/types/billing';
import type { CepLookup } from '@/lib/cep';
import { UF_OPTIONS } from '@/lib/brazil';

// The billing-address form's behavior, pure (VER-389). One reducer instead of seven
// useStates: the CEP autofill writes several fields at once but must never touch one
// the user already edited, and that interaction is unit-testable only if the whole
// draft transitions in one place.

export type BillingAddressField =
  | 'cep' | 'logradouro' | 'numero' | 'complemento' | 'bairro' | 'cidade' | 'uf';

export type BillingAddressDraft = {
  values: Record<BillingAddressField, string>;
  /** Fields the user edited by hand — autofill never overwrites these. */
  touched: ReadonlySet<BillingAddressField>;
  /** Fields THIS session's CEP lookup filled — invalidated when the CEP changes,
   * so CEP B can never be saved with CEP A's street. Stored values (an existing
   * address being edited) are not in this set and survive a CEP edit. */
  autofilled: ReadonlySet<BillingAddressField>;
  /** The normalized CEP an in-flight lookup is for; a response for any other CEP is stale. */
  lookupCep: string | null;
  lookupNotice: string | null;
  saving: boolean;
  errors: Partial<Record<BillingAddressField, string>>;
};

export type BillingAddressAction =
  | { type: 'field_changed'; field: BillingAddressField; value: string }
  | { type: 'lookup_started'; cep: string }
  | { type: 'lookup_settled'; cep: string; result: CepLookup }
  | { type: 'saving'; value: boolean }
  | { type: 'invalid'; errors: Partial<Record<BillingAddressField, string>> }
  | { type: 'reset'; address: BillingAddress | null };

export function initialBillingAddressDraft(address: BillingAddress | null): BillingAddressDraft {
  return {
    values: {
      cep: address ? formatCep(address.cep) : '',
      logradouro: address?.logradouro ?? '',
      numero: address?.numero ?? '',
      complemento: address?.complemento ?? '',
      bairro: address?.bairro ?? '',
      cidade: address?.cidade ?? '',
      uf: address?.uf ?? '',
    },
    touched: new Set(),
    autofilled: new Set(),
    lookupCep: null,
    lookupNotice: null,
    saving: false,
    errors: {},
  };
}

export function billingAddressReducer(
  state: BillingAddressDraft, action: BillingAddressAction,
): BillingAddressDraft {
  switch (action.type) {
    case 'field_changed': {
      const value = action.field === 'cep' ? formatCep(action.value) : action.value;
      const values = { ...state.values, [action.field]: value };
      let autofilled = state.autofilled;
      let lookupCep = state.lookupCep;
      if (action.field === 'cep' && normalizeCep(value) !== normalizeCep(state.values.cep)) {
        // Whatever lookup was in flight is for another CEP now — stop showing
        // its progress (the caller aborts the request itself).
        lookupCep = null;
        // The lookup that filled these fields was for ANOTHER CEP — clear what
        // it wrote (hand-edited fields stay), or a failed next lookup would
        // let CEP B be saved with CEP A's street and city.
        for (const field of state.autofilled) {
          if (!state.touched.has(field)) values[field] = '';
        }
        autofilled = new Set();
      }
      return {
        ...state,
        values,
        touched: new Set(state.touched).add(action.field),
        autofilled,
        lookupCep,
        // Editing the CEP invalidates whatever notice the previous lookup left.
        lookupNotice: action.field === 'cep' ? null : state.lookupNotice,
        errors: { ...state.errors, [action.field]: undefined },
      };
    }
    case 'lookup_started':
      return { ...state, lookupCep: action.cep, lookupNotice: null };
    case 'lookup_settled': {
      // Stale guard: the user re-edited the CEP after this lookup left. Still
      // retire the in-flight marker when it was ours, or the spinner sticks.
      if (action.cep !== state.lookupCep || action.cep !== normalizeCep(state.values.cep)) {
        return action.cep === state.lookupCep ? { ...state, lookupCep: null } : state;
      }
      if (action.result.status === 'not_found') {
        return { ...state, lookupCep: null, lookupNotice: 'CEP não encontrado. Preencha o endereço manualmente.' };
      }
      if (action.result.status === 'unavailable') {
        return { ...state, lookupCep: null, lookupNotice: 'Não conseguimos buscar o CEP agora. Preencha o endereço manualmente.' };
      }
      const filled = { ...state.values };
      const autofilled = new Set(state.autofilled);
      const autofillable: Array<[BillingAddressField, string]> = [
        ['logradouro', action.result.logradouro],
        ['bairro', action.result.bairro],
        ['cidade', action.result.cidade],
        ['uf', action.result.uf.toUpperCase()],
      ];
      for (const [field, value] of autofillable) {
        // Only fields the user has not edited — the bug every CEP form ships with.
        if (!state.touched.has(field) && value) {
          filled[field] = value;
          autofilled.add(field);
        }
      }
      return { ...state, values: filled, autofilled, lookupCep: null, lookupNotice: null, errors: clearFilled(state.errors, filled) };
    }
    case 'saving':
      return { ...state, saving: action.value };
    case 'invalid':
      return { ...state, saving: false, errors: action.errors };
    case 'reset':
      return initialBillingAddressDraft(action.address);
  }
}

function clearFilled(
  errors: BillingAddressDraft['errors'], values: Record<BillingAddressField, string>,
): BillingAddressDraft['errors'] {
  const next = { ...errors };
  for (const field of Object.keys(next) as BillingAddressField[]) {
    if (values[field].trim()) next[field] = undefined;
  }
  return next;
}

export function normalizeCep(cep: string): string {
  return cep.replace(/\D/g, '').slice(0, 8);
}

export function formatCep(cep: string): string {
  const digits = normalizeCep(cep);
  return digits.length > 5 ? `${digits.slice(0, 5)}-${digits.slice(5)}` : digits;
}

/** All failures at once — a save must name every missing field, not the first. */
export function validateBillingAddress(
  values: Record<BillingAddressField, string>,
): Partial<Record<BillingAddressField, string>> {
  const errors: Partial<Record<BillingAddressField, string>> = {};
  if (normalizeCep(values.cep).length !== 8) errors.cep = 'Informe um CEP com 8 dígitos.';
  if (!values.logradouro.trim()) errors.logradouro = 'Informe o logradouro.';
  if (!values.numero.trim()) errors.numero = 'Informe o número.';
  if (!values.bairro.trim()) errors.bairro = 'Informe o bairro.';
  if (!values.cidade.trim()) errors.cidade = 'Informe a cidade.';
  if (!(UF_OPTIONS as readonly string[]).includes(values.uf)) errors.uf = 'Selecione o estado.';
  return errors;
}

export function toSetBillingAddressRequest(
  values: Record<BillingAddressField, string>,
): SetBillingAddressRequest {
  return {
    cep: normalizeCep(values.cep),
    logradouro: values.logradouro.trim(),
    numero: values.numero.trim(),
    complemento: values.complemento.trim() || null,
    bairro: values.bairro.trim(),
    cidade: values.cidade.trim(),
    uf: values.uf,
  };
}
